/**
 * Client-side inactivity timeout (independent of Keycloak SSO idle on the server).
 * After IDLE_MS without user activity, callers show a warning; after GRACE_MS more, session ends.
 */

const MINUTE_MS = 60 * 1000;

const IDLE_MS =
  Number(process.env.REACT_APP_SESSION_IDLE_MINUTES || 20) * MINUTE_MS;
const GRACE_MS =
  Number(process.env.REACT_APP_SESSION_WARNING_MINUTES || 2) * MINUTE_MS;

const ACTIVITY_EVENTS = [
  "mousedown",
  "mousemove",
  "keydown",
  "scroll",
  "touchstart",
  "click",
];

let idleTimerId = null;
let graceTimerId = null;
let locked = false;
let started = false;

let onWarning = () => {};
let onWarningClear = () => {};
let onExpire = () => {};

function clearTimers() {
  if (idleTimerId) {
    clearTimeout(idleTimerId);
    idleTimerId = null;
  }
  if (graceTimerId) {
    clearTimeout(graceTimerId);
    graceTimerId = null;
  }
}

function scheduleIdleTimer() {
  clearTimers();
  idleTimerId = window.setTimeout(() => {
    locked = true;
    onWarning({
      idleMinutes: IDLE_MS / MINUTE_MS,
      graceMinutes: GRACE_MS / MINUTE_MS,
    });
    graceTimerId = window.setTimeout(() => {
      onExpire();
    }, GRACE_MS);
  }, IDLE_MS);
}

function onActivity() {
  if (!started || locked) {
    return;
  }
  scheduleIdleTimer();
}

export function isSessionIdleLocked() {
  return locked;
}

export function getSessionIdleConfig() {
  return {
    idleMs: IDLE_MS,
    graceMs: GRACE_MS,
    idleMinutes: IDLE_MS / MINUTE_MS,
    graceMinutes: GRACE_MS / MINUTE_MS,
  };
}

export function setSessionIdleHandlers({ onWarning: warn, onWarningClear: clear, onExpire: expire }) {
  if (warn) onWarning = warn;
  if (clear) onWarningClear = clear;
  if (expire) onExpire = expire;
}

export function startSessionIdleWatch() {
  if (started) {
    scheduleIdleTimer();
    return;
  }
  started = true;
  locked = false;
  ACTIVITY_EVENTS.forEach((eventName) => {
    window.addEventListener(eventName, onActivity, { passive: true });
  });
  scheduleIdleTimer();
}

export function stopSessionIdleWatch() {
  started = false;
  locked = false;
  clearTimers();
  ACTIVITY_EVENTS.forEach((eventName) => {
    window.removeEventListener(eventName, onActivity);
  });
  onWarningClear();
}

/** User chose to stay logged in — unlock and reset idle clock. */
export function unlockSessionIdle() {
  locked = false;
  clearTimers();
  onWarningClear();
  scheduleIdleTimer();
}
